import {
  ref as rtdbRef,
  set as rtdbSet,
  get as rtdbGet,
  onValue as rtdbOnValue,
  remove as rtdbRemove,
  goOnline as rtdbGoOnline
} from 'firebase/database';
import { auth, rtdb, writeAuditLog } from '../firebase';
import { GameHand } from '../models/FirestoreModels';

const RTDB_BASE_URL = 'https://atlcardsharks-a9914-default-rtdb.firebaseio.com';
const FAST_POLL_INTERVAL_MS = 1500;

async function getAuthTokenFast(timeoutMs: number = 800): Promise<string | null> {
  try {
    const currentUser = (auth as any)?.currentUser;
    if (!currentUser || typeof currentUser.getIdToken !== 'function') return null;
    return await Promise.race([
      currentUser.getIdToken() as Promise<string>,
      new Promise<null>((resolve) => setTimeout(() => resolve(null), timeoutMs))
    ]);
  } catch {
    return null;
  }
}

async function putRtdbRestWithAuth(path: string, payload: any, timeoutMs: number = 1500): Promise<boolean> {
  try {
    if (typeof fetch !== 'function') return false;
    const token = await getAuthTokenFast();
    if (!token) return false;

    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timer = controller ? setTimeout(() => controller.abort(), timeoutMs) : null;
    try {
      const res = await fetch(`${RTDB_BASE_URL}/${path}.json?auth=${encodeURIComponent(token)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        signal: controller?.signal
      });
      return res.ok;
    } finally {
      if (timer) clearTimeout(timer);
    }
  } catch {
    return false;
  }
}

async function getRtdbRestWithAuth<T = any>(path: string, timeoutMs: number = 1500): Promise<{ ok: boolean; data: T | null }> {
  try {
    if (typeof fetch !== 'function') return { ok: false, data: null };
    const token = await getAuthTokenFast();
    if (!token) return { ok: false, data: null };

    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timer = controller ? setTimeout(() => controller.abort(), timeoutMs) : null;
    try {
      const res = await fetch(`${RTDB_BASE_URL}/${path}.json?auth=${encodeURIComponent(token)}`, {
        method: 'GET',
        headers: { Accept: 'application/json' },
        cache: 'no-store',
        signal: controller?.signal
      });
      if (!res.ok) return { ok: false, data: null };
      const data = (await res.json()) as T | null;
      return { ok: true, data };
    } finally {
      if (timer) clearTimeout(timer);
    }
  } catch {
    return { ok: false, data: null };
  }
}

async function deleteRtdbRestWithAuth(path: string, timeoutMs: number = 1500): Promise<boolean> {
  try {
    if (typeof fetch !== 'function') return false;
    const token = await getAuthTokenFast();
    if (!token) return false;

    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timer = controller ? setTimeout(() => controller.abort(), timeoutMs) : null;
    try {
      const res = await fetch(`${RTDB_BASE_URL}/${path}.json?auth=${encodeURIComponent(token)}`, {
        method: 'DELETE',
        signal: controller?.signal
      });
      return res.ok;
    } finally {
      if (timer) clearTimeout(timer);
    }
  } catch {
    return false;
  }
}

export interface PendingScorecardSummary {
  scorecardId: string;
  creatorId: string;
  creatorName: string;
  creatorPhotoUrl?: string | null;
  partnerId?: string | null;
  partnerName?: string | null;
  opponent1Id?: string | null;
  opponent1Name?: string | null;
  opponent2Id?: string | null;
  opponent2Name?: string | null;
  team1Score: number;
  team2Score: number;
  handsCount: number;
  latestHandSummary?: string | null;
  unconfirmedHandIndex?: number | null;
  unconfirmedHandNumber?: number | null;
  isUserOpponent: boolean;
  isUserPartner: boolean;
  isSuperUser?: boolean;
  isMatchConfirmed?: boolean;
  confirmedByMemberId?: string | null;
  confirmedByMemberName?: string | null;
  confirmedAt?: string | null;
  hands?: GameHand[];
  updatedAt: number;
}

export interface LiveScorecardData {
  scorecardId: string;
  creatorId: string;
  creatorName: string;
  creatorPhotoUrl?: string | null;
  partnerId?: string | null;
  partnerName?: string | null;
  partnerPhotoUrl?: string | null;
  opponent1Id?: string | null;
  opponent1Name?: string | null;
  opponent1PhotoUrl?: string | null;
  opponent2Id?: string | null;
  opponent2Name?: string | null;
  opponent2PhotoUrl?: string | null;
  team1Score: number;
  team2Score: number;
  hands: GameHand[];
  matchStage?: 'hand1' | 'hand2' | 'hand3' | 'summary' | null;
  playedDate: string;
  locationName?: string | null;
  isMatchConfirmed?: boolean;
  confirmedByMemberId?: string | null;
  confirmedByMemberName?: string | null;
  confirmedAt?: string | null;
  updatedAt: number;
}

/**
 * Recursively strips undefined values and normalizes properties for Firebase Realtime Database.
 * RTDB throws an unhandled serialization error if any property in an object passed to set() or update() is undefined.
 */
export function sanitizeForRtdb<T>(val: T): T {
  if (val === undefined) {
    return null as any;
  }
  if (val === null || typeof val !== 'object') {
    return val;
  }
  if (Array.isArray(val)) {
    return val.map((item) => sanitizeForRtdb(item)) as any;
  }
  const result: Record<string, any> = {};
  for (const [k, v] of Object.entries(val)) {
    if (v !== undefined) {
      result[k] = sanitizeForRtdb(v);
    }
  }
  return result as any;
}

export async function publishLiveScorecard(scorecard: LiveScorecardData): Promise<void> {
  if (!scorecard.scorecardId) return;

  try {
    try {
      rtdbGoOnline(rtdb);
    } catch {}

    const nowTs = Date.now();
    const rootPath = `live_scorecards/${scorecard.scorecardId}`;
    const cleanScorecard = sanitizeForRtdb({
      scorecardId: scorecard.scorecardId,
      creatorId: scorecard.creatorId || '',
      creatorName: scorecard.creatorName || 'Member',
      creatorPhotoUrl: scorecard.creatorPhotoUrl || null,
      partnerId: scorecard.partnerId || null,
      partnerName: scorecard.partnerName || null,
      partnerPhotoUrl: scorecard.partnerPhotoUrl || null,
      opponent1Id: scorecard.opponent1Id || null,
      opponent1Name: scorecard.opponent1Name || null,
      opponent1PhotoUrl: scorecard.opponent1PhotoUrl || null,
      opponent2Id: scorecard.opponent2Id || null,
      opponent2Name: scorecard.opponent2Name || null,
      opponent2PhotoUrl: scorecard.opponent2PhotoUrl || null,
      team1Score: scorecard.team1Score || 0,
      team2Score: scorecard.team2Score || 0,
      hands: scorecard.hands || [],
      matchStage: scorecard.matchStage || 'hand1',
      playedDate: scorecard.playedDate || new Date().toISOString().slice(0, 10),
      locationName: scorecard.locationName || 'Clubhouse',
      isMatchConfirmed: Boolean(scorecard.isMatchConfirmed),
      confirmedByMemberId: scorecard.confirmedByMemberId || null,
      confirmedByMemberName: scorecard.confirmedByMemberName || null,
      confirmedAt: scorecard.confirmedAt || null,
      updatedAt: nowTs
    });

    // Identify unconfirmed hands
    let unconfirmedHandIndex: number | null = null;
    let unconfirmedHandNumber: number | null = null;
    let latestHandSummary = 'Match in progress';

    if (scorecard.hands && scorecard.hands.length > 0) {
      const lastHand = scorecard.hands[scorecard.hands.length - 1];
      const bidSuit = lastHand.trumpType === 'no_trump' ? 'NT' : (lastHand.trumpSuit?.toUpperCase() || 'TRUMP');
      latestHandSummary = `Hand ${lastHand.handNumber}: ${lastHand.bid} ${bidSuit} ${lastHand.outcome === 'made' ? 'Made' : 'Set'}`;

      const unconfirmed = scorecard.hands.find((h) => !h.isConfirmed);
      if (unconfirmed) {
        unconfirmedHandIndex = unconfirmed.handNumber - 1;
        unconfirmedHandNumber = unconfirmed.handNumber;
      }
    }

    const involved = [
      { id: scorecard.creatorId, isPartner: false, isOpponent: false },
      { id: scorecard.partnerId, isPartner: true, isOpponent: false },
      { id: scorecard.opponent1Id, isPartner: false, isOpponent: true },
      { id: scorecard.opponent2Id, isPartner: false, isOpponent: true }
    ].filter((p): p is { id: string; isPartner: boolean; isOpponent: boolean } => Boolean(p.id));

    // Fire WebSocket SDK writes in background (updates local in-memory listeners at 0ms)
    const sdkWritePromise = Promise.all([
      rtdbSet(rtdbRef(rtdb, rootPath), cleanScorecard),
      ...involved.map((player) => {
        const summary: PendingScorecardSummary = sanitizeForRtdb({
          scorecardId: scorecard.scorecardId,
          creatorId: scorecard.creatorId || '',
          creatorName: scorecard.creatorName || 'Member',
          creatorPhotoUrl: scorecard.creatorPhotoUrl || null,
          partnerId: scorecard.partnerId || null,
          partnerName: scorecard.partnerName || null,
          opponent1Id: scorecard.opponent1Id || null,
          opponent1Name: scorecard.opponent1Name || null,
          opponent2Id: scorecard.opponent2Id || null,
          opponent2Name: scorecard.opponent2Name || null,
          team1Score: scorecard.team1Score || 0,
          team2Score: scorecard.team2Score || 0,
          handsCount: scorecard.hands?.length || 0,
          latestHandSummary,
          unconfirmedHandIndex,
          unconfirmedHandNumber,
          isUserPartner: player.isPartner,
          isUserOpponent: player.isOpponent,
          isMatchConfirmed: Boolean(scorecard.isMatchConfirmed),
          confirmedByMemberId: scorecard.confirmedByMemberId || null,
          confirmedByMemberName: scorecard.confirmedByMemberName || null,
          confirmedAt: scorecard.confirmedAt || null,
          hands: scorecard.hands || [],
          updatedAt: nowTs
        });
        return rtdbSet(
          rtdbRef(rtdb, `user_live_scorecards/${player.id}/${scorecard.scorecardId}`),
          summary
        );
      })
    ]);

    // Simultaneously fire direct HTTPS REST PUT (~50ms) so cold mobile WebSockets never delay delivery
    const restSuccess = await putRtdbRestWithAuth(rootPath, cleanScorecard);
    if (!restSuccess) {
      await sdkWritePromise;
    } else {
      sdkWritePromise.catch(() => {});
    }

    console.log(
      `[liveScorecardSyncService] Published live scorecard ${scorecard.scorecardId} (REST=${restSuccess}) for ${involved.length} seated players`
    );
  } catch (err: any) {
    console.error('[liveScorecardSyncService] CRITICAL: Failed to publish live scorecard to RTDB:', err);
    try {
      await writeAuditLog(
        'Scorecard RTDB Sync Error',
        scorecard.scorecardId,
        `Failed to publish live scorecard ${scorecard.scorecardId}: ${err?.message || String(err)}`
      );
    } catch (auditErr) {
      console.warn('[liveScorecardSyncService] Could not write audit log for sync failure:', auditErr);
    }
  }
}

/**
 * Subscribes to real-time updates for a specific scorecard.
 * Used by the recorder and reviewer to watch for opponent confirmations in real time.
 */
export function subscribeToLiveScorecard(
  scorecardId: string,
  callback: (data: LiveScorecardData | null) => void
): () => void {
  if (!scorecardId) return () => {};

  try {
    rtdbGoOnline(rtdb);
  } catch {}

  let lastSig = '__INIT__';
  const emitIfChanged = (val: LiveScorecardData | null) => {
    const nextSig = JSON.stringify(val);
    if (nextSig === lastSig) return;
    lastSig = nextSig;
    callback(val);
  };

  const pullRest = () => {
    getRtdbRestWithAuth<LiveScorecardData>(`live_scorecards/${scorecardId}`)
      .then((res) => {
        if (res.ok) {
          emitIfChanged(res.data || null);
        }
      })
      .catch(() => {});
  };

  pullRest();

  const unsubscribe = rtdbOnValue(scorecardsRef(scorecardId), (snap) => {
    if (snap.exists()) {
      emitIfChanged(snap.val() as LiveScorecardData);
    } else {
      emitIfChanged(null);
    }
  });

  const intervalId = setInterval(() => {
    if (typeof document === 'undefined' || document.visibilityState === 'visible') {
      pullRest();
    }
  }, FAST_POLL_INTERVAL_MS);

  return () => {
    clearInterval(intervalId);
    unsubscribe();
  };
}

function scorecardsRef(scorecardId: string) {
  return rtdbRef(rtdb, `live_scorecards/${scorecardId}`);
}

/**
 * Robust matcher for determining whether a seated player (by id or name) matches
 * any of the user's candidate IDs or name tokens (e.g., 'Hub' matching 'Hub Harrison').
 */
export function isScorecardPlayerMatch(
  player: { id?: string | null; name?: string | null } | null | undefined,
  candidateIds: string[],
  candidateNames: string[]
): boolean {
  if (!player) return false;

  // 1. Direct ID match
  if (player.id) {
    const pId = player.id.trim().toLowerCase();
    if (pId && candidateIds.some((id) => id && id.trim().toLowerCase() === pId)) {
      return true;
    }
  }

  // 2. Tokenized & substring Name match
  if (player.name) {
    const target = player.name.trim().toLowerCase();
    if (!target) return false;
    const targetTokens = target.split(/\s+/).filter(Boolean);

    for (const rawName of candidateNames) {
      if (!rawName) continue;
      const cand = rawName.trim().toLowerCase();
      if (!cand) continue;

      // Exact match
      if (cand === target) return true;

      // Word / token match (e.g. 'hub' in 'hub harrison')
      const candTokens = cand.split(/\s+/).filter(Boolean);
      for (const cTok of candTokens) {
        if (cTok.length >= 2 && targetTokens.includes(cTok)) {
          return true;
        }
      }

      // Substring check (minimum 3 characters)
      if (cand.length >= 3 && target.includes(cand)) {
        return true;
      }
      if (target.length >= 3 && cand.includes(target)) {
        return true;
      }
    }
  }

  return false;
}

/**
 * Checks if a given player is busy according to the busy members map.
 */
export function isPlayerBusy(
  player: { id?: string | null; name?: string | null } | null | undefined,
  busyMembers: Record<string, { scorecardId: string; creatorName?: string }> | null | undefined
): { scorecardId: string; creatorName?: string } | null {
  if (!player || !busyMembers) return null;

  if (player.id) {
    const pId = player.id.trim().toLowerCase();
    if (busyMembers[player.id]) return busyMembers[player.id];
    if (busyMembers[pId]) return busyMembers[pId];
  }

  if (player.name) {
    const clean = player.name.trim().toLowerCase();
    if (busyMembers[clean]) return busyMembers[clean];
    const tokens = clean.split(/\s+/).filter(Boolean);
    for (const tok of tokens) {
      if (tok.length >= 2 && busyMembers[tok]) {
        return busyMembers[tok];
      }
    }
  }

  return null;
}

/**
 * Subscribes to pending scorecards where the logged-in user is a participant.
 * Powers the global in-app notification banner for regular members.
 */
export function subscribeToUserPendingScorecards(
  userIdOrIds: string | string[],
  userNames: string[] = [],
  callback: (scorecards: PendingScorecardSummary[]) => void
): () => void {
  const candidateIds = (Array.isArray(userIdOrIds) ? userIdOrIds : [userIdOrIds]).filter(Boolean);
  if (candidateIds.length === 0 && userNames.length === 0) return () => {};

  try {
    rtdbGoOnline(rtdb);
  } catch {}

  const allScorecardsRef = rtdbRef(rtdb, 'live_scorecards');
  let lastSig = '__INIT__';

  const processRawMap = (val: Record<string, LiveScorecardData> | null | undefined) => {
    const list: PendingScorecardSummary[] = [];
    if (val && typeof val === 'object') {
      for (const raw of Object.values(val)) {
        const sc = raw as LiveScorecardData;
        if (!sc || !sc.scorecardId) continue;

        // Skip matches created by this user (host is scoring)
        const isUserCreator = isScorecardPlayerMatch(
          { id: sc.creatorId, name: sc.creatorName },
          candidateIds,
          userNames
        );
        if (isUserCreator) continue;

        const isUserPartner = isScorecardPlayerMatch(
          { id: sc.partnerId, name: sc.partnerName },
          candidateIds,
          userNames
        );

        const isUserOpponent =
          isScorecardPlayerMatch({ id: sc.opponent1Id, name: sc.opponent1Name }, candidateIds, userNames) ||
          isScorecardPlayerMatch({ id: sc.opponent2Id, name: sc.opponent2Name }, candidateIds, userNames);

        if (!isUserPartner && !isUserOpponent) continue;

        let unconfirmedHandIndex: number | undefined = undefined;
        let unconfirmedHandNumber: number | undefined = undefined;
        let latestHandSummary = 'Match in progress';

        const rawHands: GameHand[] = sc.hands ? (Array.isArray(sc.hands) ? sc.hands : (Object.values(sc.hands) as GameHand[])) : [];

        if (rawHands.length > 0) {
          const lastHand = rawHands[rawHands.length - 1];
          const bidSuit = lastHand.trumpType === 'no_trump' ? 'NT' : (lastHand.trumpSuit?.toUpperCase() || 'TRUMP');
          latestHandSummary = `Hand ${lastHand.handNumber}: ${lastHand.bid} ${bidSuit} ${lastHand.outcome === 'made' ? 'Made' : 'Set'}`;

          const unconfirmed = rawHands.find((h: any) => !h.isConfirmed);
          if (unconfirmed) {
            unconfirmedHandIndex = unconfirmed.handNumber - 1;
            unconfirmedHandNumber = unconfirmed.handNumber;
          }
        }

        list.push({
          scorecardId: sc.scorecardId,
          creatorId: sc.creatorId,
          creatorName: sc.creatorName,
          creatorPhotoUrl: sc.creatorPhotoUrl,
          partnerId: sc.partnerId,
          partnerName: sc.partnerName,
          opponent1Id: sc.opponent1Id,
          opponent1Name: sc.opponent1Name,
          opponent2Id: sc.opponent2Id,
          opponent2Name: sc.opponent2Name,
          team1Score: sc.team1Score || 0,
          team2Score: sc.team2Score || 0,
          handsCount: rawHands.length,
          latestHandSummary,
          unconfirmedHandIndex,
          unconfirmedHandNumber,
          isUserPartner,
          isUserOpponent,
          isSuperUser: false,
          isMatchConfirmed: Boolean(sc.isMatchConfirmed),
          confirmedByMemberId: sc.confirmedByMemberId,
          confirmedByMemberName: sc.confirmedByMemberName,
          confirmedAt: sc.confirmedAt,
          hands: rawHands,
          updatedAt: sc.updatedAt || 0
        });
      }
    }

    list.sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));
    const nextSig = JSON.stringify(list);
    if (nextSig === lastSig) return;
    lastSig = nextSig;
    callback(list);
  };

  const handleSnap = (snap: any) => {
    processRawMap(snap.exists() ? snap.val() : null);
  };

  const pullRest = () => {
    getRtdbRestWithAuth<Record<string, LiveScorecardData>>('live_scorecards')
      .then((res) => {
        if (res.ok) {
          processRawMap(res.data);
        }
      })
      .catch(() => {});
  };

  // Instant HTTP REST pull (~50ms) + SDK pull so notification renders without waiting for WebSocket handshake
  pullRest();
  rtdbGet(allScorecardsRef).then(handleSnap).catch(() => {});

  const unsubscribe = rtdbOnValue(allScorecardsRef, handleSnap);

  const handleWake = () => {
    try {
      rtdbGoOnline(rtdb);
    } catch {}
    pullRest();
    rtdbGet(allScorecardsRef).then(handleSnap).catch(() => {});
  };

  const onVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      handleWake();
    }
  };

  const intervalId = setInterval(() => {
    if (typeof document === 'undefined' || document.visibilityState === 'visible') {
      pullRest();
    }
  }, FAST_POLL_INTERVAL_MS);

  if (typeof window !== 'undefined') {
    window.addEventListener('focus', handleWake);
    window.addEventListener('pageshow', handleWake);
    document.addEventListener('visibilitychange', onVisibilityChange);
  }

  return () => {
    clearInterval(intervalId);
    unsubscribe();
    if (typeof window !== 'undefined') {
      window.removeEventListener('focus', handleWake);
      window.removeEventListener('pageshow', handleWake);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    }
  };
}

/**
 * Subscribes to all active live scorecards across the club.
 * Used by Super Users / Admins to monitor and audit all in-progress matches in real time.
 */
export function subscribeToAllLiveScorecards(
  userIdOrIds: string | string[],
  callback: (scorecards: PendingScorecardSummary[]) => void,
  userNames?: string[]
): () => void {
  const candidateIds = (Array.isArray(userIdOrIds) ? userIdOrIds : [userIdOrIds]).filter(Boolean);
  const candidateNames = (userNames || []).filter(Boolean);
  try {
    rtdbGoOnline(rtdb);
  } catch {}

  const allScorecardsRef = rtdbRef(rtdb, 'live_scorecards');
  let lastSig = '__INIT__';

  const processRawMap = (val: Record<string, LiveScorecardData> | null | undefined) => {
    const list: PendingScorecardSummary[] = [];
    if (val && typeof val === 'object') {
      for (const raw of Object.values(val)) {
        const sc = raw as LiveScorecardData;
        if (!sc || !sc.scorecardId) continue;

        let unconfirmedHandIndex: number | undefined = undefined;
        let unconfirmedHandNumber: number | undefined = undefined;
        let latestHandSummary = 'Match in progress';

        const rawHands: GameHand[] = sc.hands ? (Array.isArray(sc.hands) ? sc.hands : (Object.values(sc.hands) as GameHand[])) : [];

        if (rawHands.length > 0) {
          const lastHand = rawHands[rawHands.length - 1];
          const bidSuit = lastHand.trumpType === 'no_trump' ? 'NT' : (lastHand.trumpSuit?.toUpperCase() || 'TRUMP');
          latestHandSummary = `Hand ${lastHand.handNumber}: ${lastHand.bid} ${bidSuit} ${lastHand.outcome === 'made' ? 'Made' : 'Set'}`;

          const unconfirmed = rawHands.find((h) => !h.isConfirmed);
          if (unconfirmed) {
            unconfirmedHandIndex = unconfirmed.handNumber - 1;
            unconfirmedHandNumber = unconfirmed.handNumber;
          }
        }

        const isUserPartner = isScorecardPlayerMatch(
          { id: sc.partnerId, name: sc.partnerName },
          candidateIds,
          candidateNames
        );
        const isUserOpponent =
          isScorecardPlayerMatch({ id: sc.opponent1Id, name: sc.opponent1Name }, candidateIds, candidateNames) ||
          isScorecardPlayerMatch({ id: sc.opponent2Id, name: sc.opponent2Name }, candidateIds, candidateNames);

        list.push({
          scorecardId: sc.scorecardId,
          creatorId: sc.creatorId,
          creatorName: sc.creatorName,
          creatorPhotoUrl: sc.creatorPhotoUrl,
          partnerId: sc.partnerId,
          partnerName: sc.partnerName,
          opponent1Id: sc.opponent1Id,
          opponent1Name: sc.opponent1Name,
          opponent2Id: sc.opponent2Id,
          opponent2Name: sc.opponent2Name,
          team1Score: sc.team1Score || 0,
          team2Score: sc.team2Score || 0,
          handsCount: rawHands.length,
          latestHandSummary,
          unconfirmedHandIndex,
          unconfirmedHandNumber,
          isUserPartner,
          isUserOpponent,
          isSuperUser: true,
          isMatchConfirmed: Boolean(sc.isMatchConfirmed),
          confirmedByMemberId: sc.confirmedByMemberId,
          confirmedByMemberName: sc.confirmedByMemberName,
          confirmedAt: sc.confirmedAt,
          hands: rawHands,
          updatedAt: sc.updatedAt || 0
        });
      }
    }

    list.sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));
    const nextSig = JSON.stringify(list);
    if (nextSig === lastSig) return;
    lastSig = nextSig;
    callback(list);
  };

  const handleSnap = (snap: any) => {
    processRawMap(snap.exists() ? snap.val() : null);
  };

  const pullRest = () => {
    getRtdbRestWithAuth<Record<string, LiveScorecardData>>('live_scorecards')
      .then((res) => {
        if (res.ok) {
          processRawMap(res.data);
        }
      })
      .catch(() => {});
  };

  pullRest();
  rtdbGet(allScorecardsRef).then(handleSnap).catch(() => {});

  const unsubscribe = rtdbOnValue(allScorecardsRef, handleSnap);

  const handleWake = () => {
    try {
      rtdbGoOnline(rtdb);
    } catch {}
    pullRest();
    rtdbGet(allScorecardsRef).then(handleSnap).catch(() => {});
  };

  const onVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      handleWake();
    }
  };

  const intervalId = setInterval(() => {
    if (typeof document === 'undefined' || document.visibilityState === 'visible') {
      pullRest();
    }
  }, FAST_POLL_INTERVAL_MS);

  if (typeof window !== 'undefined') {
    window.addEventListener('focus', handleWake);
    window.addEventListener('pageshow', handleWake);
    document.addEventListener('visibilitychange', onVisibilityChange);
  }

  return () => {
    clearInterval(intervalId);
    unsubscribe();
    if (typeof window !== 'undefined') {
      window.removeEventListener('focus', handleWake);
      window.removeEventListener('pageshow', handleWake);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    }
  };
}

/**
 * Subscribes to the raw live scorecards map in RTDB.
 * Used for detecting active match participation and enforcing concurrency limits.
 */
export function subscribeToLiveScorecardsMap(
  callback: (scorecardsMap: Record<string, LiveScorecardData>) => void
): () => void {
  try {
    rtdbGoOnline(rtdb);
  } catch {}

  const allScorecardsRef = rtdbRef(rtdb, 'live_scorecards');
  let lastSig = '__INIT__';

  const emitMap = (map: Record<string, LiveScorecardData>) => {
    const nextSig = JSON.stringify(map);
    if (nextSig === lastSig) return;
    lastSig = nextSig;
    callback(map);
  };

  const handleSnap = (snap: any) => {
    if (snap.exists()) {
      emitMap((snap.val() || {}) as Record<string, LiveScorecardData>);
    } else {
      emitMap({});
    }
  };

  const pullRest = () => {
    getRtdbRestWithAuth<Record<string, LiveScorecardData>>('live_scorecards')
      .then((res) => {
        if (res.ok) {
          emitMap(res.data || {});
        }
      })
      .catch(() => {});
  };

  pullRest();
  rtdbGet(allScorecardsRef).then(handleSnap).catch(() => {});

  const unsubscribe = rtdbOnValue(allScorecardsRef, handleSnap);

  const handleWake = () => {
    try {
      rtdbGoOnline(rtdb);
    } catch {}
    pullRest();
    rtdbGet(allScorecardsRef).then(handleSnap).catch(() => {});
  };

  const onVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      handleWake();
    }
  };

  const intervalId = setInterval(() => {
    if (typeof document === 'undefined' || document.visibilityState === 'visible') {
      pullRest();
    }
  }, FAST_POLL_INTERVAL_MS);

  if (typeof window !== 'undefined') {
    window.addEventListener('focus', handleWake);
    window.addEventListener('pageshow', handleWake);
    document.addEventListener('visibilitychange', onVisibilityChange);
  }

  return () => {
    clearInterval(intervalId);
    unsubscribe();
    if (typeof window !== 'undefined') {
      window.removeEventListener('focus', handleWake);
      window.removeEventListener('pageshow', handleWake);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    }
  };
}

/**
 * Checks if a user is currently participating in an active live scorecard.
 * Returns the scorecard data and user's role (creator, partner, or opponent) if found.
 */
export function findUserActiveScorecard(
  userIdOrIds: string | string[],
  scorecardsMap: Record<string, LiveScorecardData>,
  excludeScorecardId?: string,
  userNames?: string[]
): { scorecard: LiveScorecardData; role: 'creator' | 'partner' | 'opponent' } | null {
  const candidateIds = (Array.isArray(userIdOrIds) ? userIdOrIds : [userIdOrIds]).filter(Boolean);
  const candidateNames = (userNames || []).filter(Boolean);
  if (candidateIds.length === 0 && candidateNames.length === 0) return null;
  if (!scorecardsMap) return null;

  for (const sc of Object.values(scorecardsMap)) {
    if (!sc || !sc.scorecardId || sc.scorecardId === excludeScorecardId) continue;

    if (isScorecardPlayerMatch({ id: sc.creatorId, name: sc.creatorName }, candidateIds, candidateNames)) {
      return { scorecard: sc, role: 'creator' };
    }
    if (isScorecardPlayerMatch({ id: sc.partnerId, name: sc.partnerName }, candidateIds, candidateNames)) {
      return { scorecard: sc, role: 'partner' };
    }
    if (
      isScorecardPlayerMatch({ id: sc.opponent1Id, name: sc.opponent1Name }, candidateIds, candidateNames) ||
      isScorecardPlayerMatch({ id: sc.opponent2Id, name: sc.opponent2Name }, candidateIds, candidateNames)
    ) {
      return { scorecard: sc, role: 'opponent' };
    }
  }

  return null;
}

/**
 * Generates a map of all member IDs and name tokens who are actively participating in other live scorecards.
 * Used by player pickers and QR scanners to prevent adding busy players to conflicting matches.
 */
export function getBusyMembersMap(
  scorecardsMap: Record<string, LiveScorecardData>,
  excludeScorecardId?: string
): Record<string, { scorecardId: string; creatorName: string }> {
  const busy: Record<string, { scorecardId: string; creatorName: string }> = {};
  if (!scorecardsMap) return busy;

  for (const sc of Object.values(scorecardsMap)) {
    if (!sc || !sc.scorecardId || sc.scorecardId === excludeScorecardId) continue;

    const creatorName = sc.creatorName || 'Member';
    const entry = { scorecardId: sc.scorecardId, creatorName };

    const registerPlayer = (id?: string | null, name?: string | null) => {
      if (id) {
        const trimmed = id.trim();
        busy[trimmed] = entry;
        busy[trimmed.toLowerCase()] = entry;
      }
      if (name) {
        const clean = name.trim().toLowerCase();
        if (clean) {
          busy[clean] = entry;
          const tokens = clean.split(/\s+/).filter(Boolean);
          for (const tok of tokens) {
            if (tok.length >= 2) {
              busy[tok] = entry;
            }
          }
        }
      }
    };

    registerPlayer(sc.creatorId, sc.creatorName);
    registerPlayer(sc.partnerId, sc.partnerName);
    registerPlayer(sc.opponent1Id, sc.opponent1Name);
    registerPlayer(sc.opponent2Id, sc.opponent2Name);
  }

  return busy;
}

/**
 * Confirms a hand from an opponent or Super User device in real time.
 */
export async function confirmLiveScorecardHand(
  scorecardId: string,
  handIndex: number,
  confirmedByMemberId: string,
  confirmedByName: string
): Promise<void> {
  if (!scorecardId) return;

  try {
    let data: LiveScorecardData | null = null;
    const restRes = await getRtdbRestWithAuth<LiveScorecardData>(`live_scorecards/${scorecardId}`);
    if (restRes.ok && restRes.data) {
      data = restRes.data;
    } else {
      const liveRef = rtdbRef(rtdb, `live_scorecards/${scorecardId}`);
      const snap = await rtdbGet(liveRef);
      if (snap.exists()) {
        data = snap.val() as LiveScorecardData;
      }
    }

    if (!data || !data.hands || !data.hands[handIndex]) return;

    const updatedHands = [...data.hands];
    updatedHands[handIndex] = {
      ...updatedHands[handIndex],
      isConfirmed: true,
      confirmedByMemberId,
      confirmedAt: new Date().toISOString()
    };

    const updatedData: LiveScorecardData = {
      ...data,
      hands: updatedHands,
      updatedAt: Date.now()
    };

    await publishLiveScorecard(updatedData);
  } catch (err) {
    console.error('[liveScorecardSyncService] Error confirming hand:', err);
    throw err;
  }
}

/**
 * Confirms all hands in the match in real time (one-tap full match approval).
 */
export async function confirmLiveScorecardMatch(
  scorecardId: string,
  confirmedByMemberId: string,
  confirmedByName: string
): Promise<void> {
  if (!scorecardId) return;

  try {
    let data: LiveScorecardData | null = null;
    const restRes = await getRtdbRestWithAuth<LiveScorecardData>(`live_scorecards/${scorecardId}`);
    if (restRes.ok && restRes.data) {
      data = restRes.data;
    } else {
      const liveRef = rtdbRef(rtdb, `live_scorecards/${scorecardId}`);
      const snap = await rtdbGet(liveRef);
      if (snap.exists()) {
        data = snap.val() as LiveScorecardData;
      }
    }

    if (!data) return;

    const nowIso = new Date().toISOString();
    const rawHands = data.hands ? (Array.isArray(data.hands) ? data.hands : Object.values(data.hands)) : [];
    const updatedHands = rawHands.map((h: any) => ({
      ...h,
      isConfirmed: true,
      confirmedByMemberId,
      confirmedAt: h.confirmedAt || nowIso
    }));

    const updatedData: LiveScorecardData = {
      ...data,
      isMatchConfirmed: true,
      confirmedByMemberId,
      confirmedByMemberName: confirmedByName,
      confirmedAt: nowIso,
      hands: updatedHands,
      updatedAt: Date.now()
    };

    await publishLiveScorecard(updatedData);
  } catch (err) {
    console.error('[liveScorecardSyncService] Error confirming match:', err);
    throw err;
  }
}

/**
 * Clears the live scorecard and user queues when a match is finalized or cancelled.
 */
export async function removeLiveScorecard(
  scorecardId: string,
  involvedUserIds: string[]
): Promise<void> {
  if (!scorecardId) return;

  try {
    const sdkRemovalPromises = [
      rtdbRemove(rtdbRef(rtdb, `live_scorecards/${scorecardId}`))
    ];

    for (const uid of involvedUserIds) {
      if (uid) {
        sdkRemovalPromises.push(
          rtdbRemove(rtdbRef(rtdb, `user_live_scorecards/${uid}/${scorecardId}`))
        );
      }
    }

    const restDeleted = await deleteRtdbRestWithAuth(`live_scorecards/${scorecardId}`);
    if (!restDeleted) {
      await Promise.all(sdkRemovalPromises);
    } else {
      Promise.all(sdkRemovalPromises).catch(() => {});
    }

    console.log(`[liveScorecardSyncService] Removed live scorecard ${scorecardId}`);
  } catch (err: any) {
    console.error('[liveScorecardSyncService] Error removing live scorecard:', err);
    try {
      await writeAuditLog(
        'Scorecard RTDB Remove Error',
        scorecardId,
        `Failed to remove live scorecard ${scorecardId}: ${err?.message || String(err)}`
      );
    } catch {}
  }
}
